import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ResellerId } from "@KassaroAB/shared/types/ids";
import { getAdminResellerInvoicesApi } from "../api/adminResellerInvoicesApi";
import {
  generateResellerInvoiceApi,
  markResellerInvoiceAsSentApi,
} from "../api/invoicesApi";

const GENERATE_CONCURRENCY = 3;

export const adminResellerInvoicesBaseKey = ["admin", "resellerInvoices"];

export const useGetAdminResellerInvoicesQuery = (period: string) => {
  return useQuery({
    queryKey: [...adminResellerInvoicesBaseKey, period],
    queryFn: () => getAdminResellerInvoicesApi(period),
  });
};

export const useGenerateResellerInvoicesMutation = (
  period: string,
  onProgress: (done: number) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (resellerIds: ResellerId[]) => {
      const failed: ResellerId[] = [];
      const queue = [...resellerIds];
      let done = 0;
      onProgress(done);
      const worker = async () => {
        while (queue.length > 0) {
          const resellerId = queue.shift()!;
          await generateResellerInvoiceApi(resellerId, period).catch(() => {
            failed.push(resellerId);
          });
          done += 1;
          onProgress(done);
        }
      };
      await Promise.all(Array.from({ length: GENERATE_CONCURRENCY }, worker));
      return failed;
    },
    onSettled: (_data, _error, resellerIds) => {
      queryClient.invalidateQueries({ queryKey: adminResellerInvoicesBaseKey });
      resellerIds.forEach((resellerId) =>
        queryClient.invalidateQueries({
          queryKey: ["resellers", resellerId, "invoices"],
        }),
      );
    },
  });
};

export const useSendResellerInvoiceToBokioMutation = (period: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resellerId: ResellerId) =>
      markResellerInvoiceAsSentApi(resellerId, period),
    onSettled: (_data, _error, resellerId) => {
      queryClient.invalidateQueries({ queryKey: adminResellerInvoicesBaseKey });
      queryClient.invalidateQueries({
        queryKey: ["resellers", resellerId, "invoices"],
      });
    },
  });
};
